import { useTrackStore } from '.'
import type { SegmentCollection } from '@ve-pro/types/track.type'
import hotkeys from 'hotkeys-js'
import _ from 'lodash'
import {
  isSegmentGroupChild,
  getGroupBySegment,
} from '@ve-pro/store/tracks/segment/segment-group'

export function triggerHotKeys(
  event: MouseEvent | TouchEvent | PointerEvent,
): void {
  if (
    [event.ctrlKey, event.metaKey, event.shiftKey].filter((t) => t).length === 1
  ) {
    const event = new KeyboardEvent('keydown', {
      key: 'Shift',
      which: 16,
      keyCode: 16,
      shiftKey: true,
    })
    document.dispatchEvent(event)
  }
}

/**
 * 判断是否单独按下command或者shift键
 * @returns Boolean
 */
export function isPressedCommandOrShift(): boolean {
  const isSingle = hotkeys.getPressedKeyCodes().length === 1
  return (
    isSingle &&
    (hotkeys.isPressed('command') ||
      hotkeys.isPressed('ctrl') ||
      hotkeys.isPressed('shift'))
  )
}

/**
 * 获取默认的activeSegment
 * @param ids SegmentCollection['id'][]
 * @returns
 */
export function getDefaultActiveSegment(
  ids: SegmentCollection['id'][],
): SegmentCollection | null {
  const trackStore = useTrackStore()
  const allSegments = [...trackStore.segments, ...trackStore.subtitleGroups]

  const activeSegment =
    trackStore.activeSegment && ids.includes(trackStore.activeSegment.id)
      ? trackStore.activeSegment
      : allSegments.find((seg) => seg.id === ids[0]) ?? null
  return activeSegment
}

/**
 * 更新activeSegment和activeSegments的值
 * @param segment SegmentCollection
 * @param activeSegmentIds SegmentCollection['id'][]
 */
export function setActiveSegment(
  segment?: SegmentCollection | null,
  activeSegmentIds?: SegmentCollection['id'][],
): void {
  const trackStore = useTrackStore()

  if (!segment) {
    trackStore.activeSegment = null
    trackStore.activeSegments = []
    return
  }

  trackStore.activeSegment = segment

  if (activeSegmentIds) {
    trackStore.activeSegments = _.uniq([
      ...activeSegmentIds,
      trackStore.activeSegment.id,
    ])

    excludeDisabledSelectAll()
  } else {
    trackStore.activeSegments = [segment.id]
  }
}

export function toggleActiveSegment(
  segment: SegmentCollection,
  options?: { actived?: boolean; multiple?: boolean },
): void {
  const { actived, multiple } = options ?? {}
  const trackStore = useTrackStore()

  const isMultiple = multiple || isPressedCommandOrShift()

  // 单选的取消
  if (actived === false) {
    if (isMultiple) {
      if (trackStore.activeSegment?.id === segment.id) {
        trackStore.activeSegment =
          trackStore.segments.find(
            (seg) => seg.id === trackStore.activeSegments[0],
          ) ?? null
      }
      trackStore.activeSegments = trackStore.activeSegments.filter(
        (id) => id !== segment.id,
      )
    } else {
      trackStore.activeSegment = null
      trackStore.activeSegments = []
    }
    return
  }

  if (isMultiple) {
    if (trackStore.activeSegments.includes(segment.id)) {
      // 取消选中
      if (actived !== true) {
        trackStore.activeSegments = trackStore.activeSegments.filter(
          (id) => id !== segment.id,
        )
        if (trackStore.activeSegment?.id === segment.id) {
          trackStore.activeSegment =
            trackStore.segments.find(
              (seg) => seg.id === trackStore.activeSegments[0],
            ) ?? null
        }
      }
    } else {
      // 多选
      trackStore.activeSegment = segment
      trackStore.activeSegments.push(segment.id)
    }

    excludeDisabledSelectAll()
  } else {
    trackStore.activeSegment = segment
    trackStore.activeSegments = [segment.id]
  }
}

function excludeDisabledSelectAll() {
  const trackStore = useTrackStore()
  // 过滤掉不支持多选的元件，比如转场元件
  const allSegments = [...trackStore.segments, ...trackStore.subtitleGroups]
  trackStore.activeSegments = trackStore.activeSegments.filter(
    (id) =>
      allSegments.find((seg) => seg.id === id)?.behavior.selectAll?.enabled,
  )
  if (!trackStore.activeSegment?.behavior.selectAll?.enabled) {
    trackStore.activeSegment =
      allSegments.find((seg) => seg.id === trackStore.activeSegments[0]) ?? null
  }
}

/**
 * 从框选的segments中选出activeSegment
 */
export function toggleActiveSegmentFromSegments(
  segments: SegmentCollection[],
): void {
  const trackStore = useTrackStore()

  if (!segments.length) {
    return
  }

  // 按下command或shift键时，合并选中的segments
  if (isPressedCommandOrShift()) {
    trackStore.activeSegments = _.uniq([
      ...trackStore.activeSegments,
      ...segments.map((segment) => segment.id),
    ])
  } else {
    trackStore.activeSegments = segments.map((segment) => segment.id)
  }

  // 如果activeSegment为空或不在框选的segments中，则选中第一个
  if (
    !trackStore.activeSegment ||
    !segments.some((seg) => seg.id === trackStore.activeSegment?.id)
  ) {
    trackStore.activeSegment = segments[0]!
  }

  // 如果activeSegment在框选的segments中，则...
  trackStore.activeSegment = segments[0]!
}

/**
 * 判断是否是组合或普通元件去设置activeSegment
 * @param segment SegmentCollection
 */
export function setActive(segment: SegmentCollection): void {
  const trackStore = useTrackStore()

  if (isSegmentGroupChild(segment)) {
    const group = getGroupBySegment(segment)

    if (group) {
      trackStore.activeSegmentGroup = group
      setActiveSegment(group, [segment.id])
    }
  } else {
    setActiveSegment(segment)
  }
}
